// Centralized query keys.
//
// Param-keyed helpers accept `string | undefined`: while a param is still
// resolving, the owning hook passes TanStack's `skipToken` as its `queryFn`,
// so the resulting key (e.g. `['job', undefined]`) is a disabled placeholder
// that never fetches and never stores data. Cache *writers* (`setQueryData`,
// e.g. the SSE subscriptions) must only build keys from defined params.
// (The runFiles/runArtifacts/runSummary helpers stay `string`-strict: their
// hooks require resolved ids and gate on an explicit `enabled` arg instead.)
export const queryKeys = {
  me: () => ['me'] as const,
  jobs: (userId: string | undefined) => ['jobs', userId] as const,
  job: (id: string | undefined) => ['job', id] as const,
  runs: (jobId: string | undefined) => ['runs', jobId] as const,
  run: (jobId: string | undefined, runId: string | undefined) => ['run', jobId, runId] as const,
  runJournal: (jobId: string | undefined, runId: string | undefined) =>
    ['runJournal', jobId, runId] as const,
  runAccess: (jobId: string | undefined, runId: string | undefined) =>
    ['runAccess', jobId, runId] as const,
  runFiles: (jobId: string, runId: string) => ['runFiles', jobId, runId] as const,
  runArtifacts: (jobId: string, runId: string) => ['runArtifacts', jobId, runId] as const,
  runSummary: (jobId: string, runId: string) => ['runSummary', jobId, runId] as const,
  integrations: () => ['integrations'] as const,
  // Prefix matching every admin query — for bulk invalidation after admin mutations.
  adminPrefix: () => ['admin'] as const,
  adminUsers: () => ['admin', 'users'] as const,
  adminUser: (userId: string | undefined) => ['admin', 'user', userId] as const,
  adminJobs: (filters: Record<string, string | undefined> = {}) =>
    ['admin', 'jobs', filters] as const,
  adminUserJobs: (userId: string | undefined, state?: string) =>
    ['admin', 'user', userId, 'jobs', state] as const,
  adminJob: (jobId: string | undefined) => ['admin', 'job', jobId] as const,
  adminRuns: (filters: Record<string, string | undefined> = {}) =>
    ['admin', 'runs', filters] as const,
  adminUserRuns: (userId: string | undefined, filters: Record<string, string | undefined> = {}) =>
    ['admin', 'user', userId, 'runs', filters] as const,
  adminRun: (runId: string | undefined) => ['admin', 'run', runId] as const,
  adminUserIntegrations: (userId: string | undefined) =>
    ['admin', 'user', userId, 'integrations'] as const,
};
